import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import Choices from 'choices.js';
import 'choices.js/public/assets/styles/choices.min.css';
import './ChoicesSelect.css';

interface ChoicesOption {
  value: string;
  label: string;
  disabled?: boolean;
  selected?: boolean;
  customProperties?: any;
}

interface ChoicesSelectProps {
  options: ChoicesOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  searchEnabled?: boolean;
  removeItemButton?: boolean;
  allowHTML?: boolean;
  shouldSort?: boolean;
}

export interface ChoicesSelectRef {
  destroy: () => void;
  setValue: (value: string) => void;
  getValue: () => string;
}

const ChoicesSelect = forwardRef<ChoicesSelectRef, ChoicesSelectProps>(({
  options,
  value = '',
  onChange,
  placeholder = 'Choose an option',
  disabled = false,
  className = '',
  searchEnabled = false, // Default to false for consistency
  removeItemButton = false,
  allowHTML = false, // Default to false for security and consistency
  shouldSort = false
}, ref) => {
  const selectRef = useRef<HTMLSelectElement>(null);
  const choicesRef = useRef<Choices | null>(null);

  useImperativeHandle(ref, () => ({
    destroy: () => {
      if (choicesRef.current) {
        choicesRef.current.destroy();
        choicesRef.current = null;
      }
    },
    setValue: (newValue: string) => {
      if (choicesRef.current) {
        choicesRef.current.setChoiceByValue(newValue);
      }
    },
    getValue: () => {
      if (choicesRef.current) {
        return choicesRef.current.getValue(true) as string;
      }
      return '';
    }
  }));

  useEffect(() => {
    if (!selectRef.current) return;

    const selectElement = selectRef.current;

    // Initialize Choices.js
    choicesRef.current = new Choices(selectElement, {
      searchEnabled,
      removeItemButton,
      allowHTML,
      shouldSort,
      placeholder: true,
      placeholderValue: placeholder,
      itemSelectText: '',
      classNames: {
        containerOuter: 'choices',
        containerInner: 'choices__inner',
        input: 'choices__input',
        inputCloned: 'choices__input--cloned',
        list: 'choices__list',
        listItems: 'choices__list--multiple',
        listSingle: 'choices__list--single',
        listDropdown: 'choices__list--dropdown',
        item: 'choices__item',
        itemSelectable: 'choices__item--selectable',
        itemDisabled: 'choices__item--disabled',
        itemChoice: 'choices__item--choice',
        placeholder: 'choices__placeholder',
        group: 'choices__group',
        groupHeading: 'choices__heading',
        button: 'choices__button',
        activeState: 'is-active',
        focusState: 'is-focused',
        openState: 'is-open',
        disabledState: 'is-disabled',
        highlightedState: 'is-highlighted',
        selectedState: 'is-selected',
        flippedState: 'is-flipped',
        loadingState: 'is-loading',
        noResults: 'has-no-results',
        noChoices: 'has-no-choices',
        description: 'choices__description',
        notice: 'choices__notice',
        addChoice: 'choices__item--add-choice'
      } as any
    });

    // Set initial value
    if (value) {
      choicesRef.current.setChoiceByValue(value);
    }

    // Add change event listener
    const handleChange = (event: any) => {
      if (onChange) {
        onChange(event.detail.value);
      }
    };

    selectElement.addEventListener('change', handleChange);

    // Cleanup function
    return () => {
      selectElement.removeEventListener('change', handleChange);
      if (choicesRef.current) {
        choicesRef.current.destroy();
        choicesRef.current = null;
      }
    };
  }, [searchEnabled, removeItemButton, allowHTML, shouldSort, placeholder, onChange, value]);

  // Update options when they change
  useEffect(() => {
    if (choicesRef.current) {
      choicesRef.current.clearStore();
      choicesRef.current.setChoices(
        options.map(option => ({
          value: option.value,
          label: option.label,
          disabled: option.disabled || false,
          selected: option.selected || option.value === value,
          customProperties: option.customProperties
        })),
        'value',
        'label',
        true
      );
    }
  }, [options, value]);

  // Update value when it changes externally
  useEffect(() => {
    if (choicesRef.current && value !== undefined) {
      const currentValue = choicesRef.current.getValue(true);
      if (currentValue !== value) {
        choicesRef.current.setChoiceByValue(value);
      }
    }
  }, [value]);

  // Update disabled state
  useEffect(() => {
    if (choicesRef.current) {
      if (disabled) {
        choicesRef.current.disable();
      } else {
        choicesRef.current.enable();
      }
    }
  }, [disabled]);

  return (
    <div className={className}>
      <select
        ref={selectRef}
        className="hidden"
        disabled={disabled}
      >
        <option value="">{placeholder}</option>
        {options.map((option, index) => (
          <option
            key={`${option.value}-${index}`}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
});

ChoicesSelect.displayName = 'ChoicesSelect';

export default ChoicesSelect;
